import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Popup } from 'react-map-gl/maplibre'
import style from './AreaTable.module.css'
import {
    TableMode, AreaRow, AlertLevel, SeaConditionLevel, LightningRisk, WarningType, ColumnDef,
    ALERT_LEVELS, SEA_CONDITION_LEVELS, LIGHTNING_LEVELS, WARNING_COLUMNS, ALERT_RANK, actionFor,
} from './AreaTableType'
import InputGroup from '../inputComp/InputGroup'
import { SearchIcon } from '../iconSet/IconSet'
import { useAdminData } from '../../context/LocationDataContext'   // adjust path
import { buildRows } from './areaRows'
import PolygonSelectTool from './PolygonSelectTool'
import { Pt, cleanPoints, centreOf, polygonHitsGeometry } from './polygonHit'
import LocationMap from '../map/LocationMap'                      // adjust path
import { ActiveEW, AreaColors, UkAdminProps } from '../map/LocationMapTypes' // adjust path
import { getActiveEarlyWarning } from '../map/ukAdminApi'                // adjust path

interface AreaTableProps {
    warningType: WarningType
    defaultStart?: string
    defaultEnd?: string
    previousAlerts?: Record<string, AlertLevel>   // keyed by area code
}

// lets the parent (EW) read the rows the user ticked when "save & submit" is pressed
export interface AreaTableHandle {
    getSelectedRows: () => AreaRow[]
}

// IMPORTANT: stable reference. A `= {}` default inside the props creates a new object
// every render, which re-fires any effect that depends on it and causes an infinite loop.
const NO_ALERTS: Record<string, AlertLevel> = {}

// map fill per new alert level (same colours as the warning map)
const ALERT_FILL: Partial<Record<AlertLevel, string>> = {
    Yellow: '#facc15',
    Amber: '#f59e0b',
    Red: '#dc2626',
}
const SELECTED_FILL = '#8b8fd9'   // selected but no alert level chosen yet

// active warning severity from the API -> table alert level
const SEVERITY_TO_ALERT: Record<string, AlertLevel> = {
    yellow: 'Yellow' as AlertLevel,
    amber: 'Amber' as AlertLevel,
    orange: 'Amber' as AlertLevel,
    red: 'Red' as AlertLevel,
}

// active warnings of this type -> { areaCode: highest alert level }
const activeLevelsFor = (list: ActiveEW[], type: string): Record<string, AlertLevel> => {
    const out: Record<string, AlertLevel> = {}
    const wanted = type.trim().toUpperCase()

    list.forEach((w) => {
        if ((w.warningType ?? '').trim().toUpperCase() !== wanted) return
        const level = SEVERITY_TO_ALERT[(w.severity ?? '').trim().toLowerCase()]
        if (!level || !w.geoId) return
        const current = out[w.geoId]
        if (!current || ALERT_RANK[level] > ALERT_RANK[current]) out[w.geoId] = level
    })
    return out
}

// Rows are in tree order (parent, then its children, then their children).
// Moves every block (a row + everything under it) that contains an active warning to the top
// of its level, keeping tree order so guide lines, hiding and locking still work.
// Blocks without a warning keep their original (alphabetical) order.
const warnedFirst = (list: AreaRow[]): AreaRow[] => {
    const sortLevel = (items: AreaRow[], depth: number): AreaRow[] => {
        const blocks: AreaRow[][] = []
        items.forEach((r) => {
            if (r.depth <= depth || blocks.length === 0) blocks.push([r])
            else blocks[blocks.length - 1].push(r)
        })

        return blocks
            .map((b, i) => {
                const block = [b[0], ...sortLevel(b.slice(1), b[0].depth + 1)]
                return { block, i, warned: block.some((r) => r.previousAlert !== 'None') }
            })
            .sort((a, b) => (a.warned === b.warned ? a.i - b.i : a.warned ? -1 : 1))
            .flatMap((x) => x.block)
    }

    return sortLevel(list, list.length ? Math.min(...list.map((r) => r.depth)) : 0)
}

// popup info for the clicked area
interface MapPick {
    rowId: string
    lng: number
    lat: number
    clickedName: string           // what the user actually clicked (a ward may map to its district row)
}

// result of a drawn shape
interface BulkPick {
    rowIds: string[]              // table rows covered by the shape
    level: number                 // admin level used (from zoom)
    hitCount: number              // map areas touched at that level
    lng: number
    lat: number
}

// same zoom bands as LocationMap
const levelForZoom = (z: number): 1 | 2 | 3 | 4 => (z < 5.5 ? 1 : z < 7 ? 2 : z < 9 ? 3 : 4)
const LEVEL_LABEL: Record<number, string> = { 1: 'countries', 2: 'counties / unitary authorities', 3: 'districts', 4: 'wards' }

// page numbers with "…" gaps so hundreds of pages don't make hundreds of buttons
const getPages = (page: number, total: number): (number | '…')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

    const nums = Array.from(new Set([1, total, page - 1, page, page + 1]))
        .filter((n) => n >= 1 && n <= total)
        .sort((a, b) => a - b)

    const result: (number | '…')[] = []
    nums.forEach((n, i) => {
        if (i > 0 && n - nums[i - 1] > 1) result.push('…')
        result.push(n)
    })
    return result
}

// Rows are in tree order (parent, then its children, then their children).
// Any row sitting under a selected row is unselected, so a hidden child can never stay selected.
const clearSelectedChildren = (list: AreaRow[]): AreaRow[] => {
    let owner: AreaRow | null = null

    return list.map((r) => {
        if (owner && r.depth > owner.depth) {
            return r.selected ? { ...r, selected: false } : r
        }
        owner = r.selected ? r : null
        return r
    })
}

const AreaTable = forwardRef<AreaTableHandle, AreaTableProps>(({
    warningType,
    defaultStart = '',
    defaultEnd = '',
    previousAlerts = NO_ALERTS,
}, ref) => {
    const { data, loading, error } = useAdminData()

    // already-active warnings of this type: { areaCode: alert level }
    const [activeLevels, setActiveLevels] = useState<Record<string, AlertLevel>>(NO_ALERTS)

    const [tableMode, setTableMode] = useState<TableMode>('TABLE')
    const [rows, setRows] = useState<AreaRow[]>([])
    const [rowCount, setRowCount] = useState(10)
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [levelFilter, setLevelFilter] = useState<string>('ALL')
    const [mapPick, setMapPick] = useState<MapPick | null>(null)

    // draw-a-shape selection
    const [drawing, setDrawing] = useState<boolean>(false)
    const [drawPts, setDrawPts] = useState<Pt[]>([])
    const drawPtsRef = useRef<Pt[]>([])
    const [bulk, setBulk] = useState<BulkPick | null>(null)
    const [bulkDraft, setBulkDraft] = useState<AreaRow | null>(null)

    const rowCountRef = useRef<HTMLInputElement | null>(null)
    const searchRef = useRef<HTMLInputElement | null>(null)

    const columns = WARNING_COLUMNS[warningType]

    // grid: checkbox, name, level, then one width per dynamic column (date columns are wider)
    const gridStyle = useMemo(() => {
        const widths = columns.map((c) =>
            c.kind === 'startDate' || c.kind === 'endDate' ? 12 : 8.3
        )
        const template = widths.map((w) => `minmax(${w}rem, 1fr)`).join(' ')
        const min = 3 + 14 + 9 + widths.reduce((a, b) => a + b, 0) + (widths.length + 3) * 0.5

        return {
            '--cols': `3rem minmax(14rem, 1.5fr) minmax(9rem, 1fr) ${template}`,
            '--min': `${min}rem`,
        } as React.CSSProperties
    }, [columns])

    // the row count input only exists in table mode, so set it whenever that mode opens
    useEffect(() => {
        if (tableMode === 'TABLE' && rowCountRef.current) rowCountRef.current.value = String(rowCount)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableMode])

    // build the tree once per data load
    const baseRows = useMemo(() => (data ? buildRows(data) : []), [data])

    // previous alert for an area: value passed in by the parent wins, then the active warning
    const prevAlertFor = (code: string): AlertLevel =>
        previousAlerts[code] ?? activeLevels[code] ?? ('None' as AlertLevel)

    // load the warnings that are already active for this warning type
    useEffect(() => {
        const ctrl = new AbortController()
        setActiveLevels(NO_ALERTS)

        getActiveEarlyWarning(ctrl.signal)
            .then((list) => setActiveLevels(activeLevelsFor(list, warningType)))
            .catch((err) => {
                if (!ctrl.signal.aborted) console.error('Active warnings failed to load', err)
            })

        return () => ctrl.abort()
    }, [warningType])

    // start fresh when data or the warning type changes, using the form values as the starting values
    useEffect(() => {
        setRows(warnedFirst(baseRows.map((r) => ({
            ...r,
            startDate: defaultStart,
            endDate: defaultEnd,
            previousAlert: prevAlertFor(r.code),
        }))))
        setPage(1)
        setMapPick(null)
        setBulk(null)
        setDrawing(false)
        drawPtsRef.current = []
        setDrawPts([])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseRows, warningType])

    // previous alerts changed -> refresh the "previous alert" column and move warned areas to the top
    useEffect(() => {
        setRows((prev) => warnedFirst(prev.map((r) => ({ ...r, previousAlert: prevAlertFor(r.code) }))))
        setPage(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousAlerts, activeLevels])

    // pre-select every area that already has an active warning of this type.
    // a selected parent still hides (and unselects) its children, same as a manual tick.
    useEffect(() => {
        if (Object.keys(activeLevels).length === 0) return
        setRows((prev) => clearSelectedChildren(prev.map((r) =>
            activeLevels[r.code] ? { ...r, selected: true } : r
        )))
    }, [activeLevels, baseRows])

    // form start time changed -> every row gets it (rows can still be edited one by one afterwards)
    useEffect(() => {
        setRows((prev) => prev.map((r) => ({ ...r, startDate: defaultStart })))
    }, [defaultStart])

    // form end time changed -> every row gets it
    useEffect(() => {
        setRows((prev) => prev.map((r) => ({ ...r, endDate: defaultEnd })))
    }, [defaultEnd])

    useImperativeHandle(ref, () => ({
        getSelectedRows: () => rows.filter((r) => r.selected),
    }), [rows])

    const searchData = (event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value)
        setPage(1)
    }

    const changeRowCount = (event: ChangeEvent<HTMLInputElement>) => {
        const value = Number(event.target.value)
        if (!value) return
        setRowCount(Math.min(30, Math.max(5, value)))
        setPage(1)
    }

    // update one field of one row.
    // editing the level of a continued row turns it into an upgrade / downgrade
    const updateRow = (id: string, patch: Partial<AreaRow>) =>
        setRows((prev) => prev.map((r) => {
            if (r.id !== id) return r
            const next = { ...r, ...patch }
            if (patch.newAlert !== undefined && patch.newAlert !== r.previousAlert) next.continued = false
            if (patch.newAlert !== undefined && patch.newAlert === r.previousAlert && r.previousAlert !== 'None') next.continued = true
            return next
        }))


    // unticking a row puts its values back to the start values; previous alert is kept
    const resetRow = (r: AreaRow): AreaRow => ({
        ...r,
        selected: false,
        continued: false,
        newAlert: 'None',
        rainfall: 0,
        minWindSpeed: 0,
        maxWindSpeed: 0,
        minWaveHeight: 0,
        maxWaveHeight: 0,
        seaCondition: 'None',
        lightning: 'None',
        startDate: defaultStart,      // back to the form's validity times
        endDate: defaultEnd,
    })

    // checking a row hides everything under it; unchecking clears its values
    const toggleRow = (id: string, checked: boolean) =>
        !isLocked(id) && setRows((prev) =>
            clearSelectedChildren(prev.map((r) =>
                r.id !== id ? r : checked ? { ...r, selected: true } : resetRow(r)
            ))
        )

    // rows hidden because an ancestor is selected, how many each selected row hides,
    // and which selected row "owns" each hidden row
    const { hiddenIds, hiddenCount, ownerOf } = useMemo(() => {
        const hidden = new Set<string>()
        const count = new Map<string, number>()
        const owners = new Map<string, string>()
        let owner: AreaRow | null = null

        for (const r of rows) {
            if (owner && r.depth > owner.depth) {
                hidden.add(r.id)
                owners.set(r.id, owner.id)
                count.set(owner.id, (count.get(owner.id) ?? 0) + 1)
                continue
            }
            owner = r.selected ? r : null
        }
        return { hiddenIds: hidden, hiddenCount: count, ownerOf: owners }
    }, [rows])

    // every ancestor of a selected row is locked: greyed out, cannot be ticked or edited.
    // value = how many selected rows sit somewhere under it
    const lockedCount = useMemo(() => {
        const byId = new Map(rows.map((r) => [r.id, r]))
        const count = new Map<string, number>()
        rows.forEach((r) => {
            if (!r.selected) return
            let parentId = r.parentId
            while (parentId) {
                count.set(parentId, (count.get(parentId) ?? 0) + 1)
                parentId = byId.get(parentId)?.parentId ?? null
            }
        })
        return count
    }, [rows])

    const isLocked = (id: string) => (lockedCount.get(id) ?? 0) > 0

    /* ---------- continue existing active warnings ---------- */

    // visible areas that already have an active warning of this type
    const activeRows = useMemo(
        () => rows.filter((r) => r.previousAlert !== 'None' && !hiddenIds.has(r.id)),
        [rows, hiddenIds]
    )
    const continuedCount = activeRows.filter((r) => r.continued).length
    const changedCount = activeRows.filter((r) => r.selected && !r.continued && r.newAlert !== 'None').length

    // copy the previous severity into the new alert and keep the row selected
    const continueRow = (r: AreaRow): AreaRow =>
        r.previousAlert === 'None' ? r : { ...r, selected: true, continued: true, newAlert: r.previousAlert }

    const continueOne = (id: string) =>
        !isLocked(id) && setRows((prev) =>
            clearSelectedChildren(prev.map((r) => (r.id === id ? continueRow(r) : r)))
        )

    const continueAll = () =>
        setRows((prev) =>
            clearSelectedChildren(prev.map((r) =>
                r.previousAlert !== 'None' && !isLocked(r.id) && !hiddenIds.has(r.id) ? continueRow(r) : r
            ))
        )

    // reset the values of active-warning areas but keep them ticked
    const clearActive = () =>
        setRows((prev) => prev.map((r) =>
            r.previousAlert !== 'None' && r.selected && !isLocked(r.id) ? { ...resetRow(r), selected: true } : r
        ))

    // badge text for a selected active row
    const actionLabel = (r: AreaRow): string | null => {
        if (!r.selected || r.previousAlert === 'None') return null
        if (r.newAlert === 'None') return 'Set level'
        const a = actionFor(r)
        return a === 'CONTINUE' ? 'Continue' : a === 'UPGRADE' ? 'Upgrade' : 'Downgrade'
    }

    // for every visible row: does it have a later sibling, and which ancestor lines keep going
    const treeGuides = useMemo(() => {
        const list = rows.filter((r) => !hiddenIds.has(r.id))

        // walking backwards: has this depth appeared later before a shallower row?
        const hasNext: boolean[] = new Array(list.length).fill(false)
        const seen: boolean[] = []
        for (let i = list.length - 1; i >= 0; i--) {
            const d = list[i].depth
            hasNext[i] = !!seen[d]
            seen[d] = true
            seen.length = d + 1          // forget deeper levels
        }

        // walking forwards: remember hasNext of the current ancestor at each depth
        const anc: boolean[] = []
        const map = new Map<string, { ancestors: boolean[]; hasNext: boolean }>()
        list.forEach((r, i) => {
            anc[r.depth] = hasNext[i]
            map.set(r.id, { ancestors: anc.slice(1, r.depth), hasNext: hasNext[i] })
        })
        return map
    }, [rows, hiddenIds])

    // level types that actually exist in the data
    const levelOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.level))).sort(), [rows])

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase()
        const visibleRows = rows.filter((r) => !hiddenIds.has(r.id))
        // no search: normal view with level filter
        if (!term) {
            return visibleRows.filter((r) => levelFilter === 'ALL' || r.level === levelFilter)
        }
        const byId = new Map(rows.map((r) => [r.id, r]))
        const keep = new Set<string>()
        visibleRows.forEach((r) => {
            const matches = r.name.toLowerCase().includes(term) || r.code.toLowerCase().includes(term)
            const levelOk = levelFilter === 'ALL' || r.level === levelFilter
            if (!matches || !levelOk) return
            // keep the match and every parent above it
            keep.add(r.id)
            let parentId = r.parentId
            while (parentId) {
                keep.add(parentId)
                parentId = byId.get(parentId)?.parentId ?? null
            }
        })
        // rows already stay in tree order, so parents always come before their children
        return visibleRows.filter((r) => keep.has(r.id))
    }, [rows, hiddenIds, search, levelFilter])

    const isMatch = (r: AreaRow) => {
        const term = search.trim().toLowerCase()
        return !!term && (r.name.toLowerCase().includes(term) || r.code.toLowerCase().includes(term))
    }

    const totalPages = Math.max(1, Math.ceil(filtered.length / rowCount))
    const visible = filtered.slice((page - 1) * rowCount, page * rowCount)

    // the table gets shorter when rows are hidden, so keep the page number valid
    useEffect(() => {
        if (page > totalPages) setPage(totalPages)
    }, [page, totalPages])

    /* ---------- map mode ---------- */

    const rowByCode = useMemo(() => {
        const m = new Map<string, AreaRow>()
        rows.forEach((r) => { if (r.code) m.set(r.code, r) })
        return m
    }, [rows])

    // map fill: new alert colour, or a highlight for selected rows; hidden children follow their owner
    const mapColors = useMemo<AreaColors>(() => {
        const byId = new Map(rows.map((r) => [r.id, r]))
        const colors: AreaColors = {}
        rows.forEach((r) => {
            if (!r.code) return
            const owner = ownerOf.has(r.id) ? byId.get(ownerOf.get(r.id)!) : undefined
            const src = owner ?? r
            // new alert wins; otherwise the area keeps its previous (active) alert colour
            const fill = ALERT_FILL[src.newAlert]
                ?? ALERT_FILL[r.previousAlert]
                ?? ALERT_FILL[src.previousAlert]
                ?? (src.selected ? SELECTED_FILL : undefined)
            if (fill) colors[r.code] = fill
        })
        return colors
    }, [rows, ownerOf])

    // clicked area -> its row. Wards (level 4) are not in the table, so fall back to the district / county row.
    const onMapAreaClick = (area: UkAdminProps, lngLat: { lng: number; lat: number }) => {
        if (drawing || bulk) return
        const props = area as UkAdminProps & { adm2?: string; adm3?: string }
        const row = rowByCode.get(area.code)
            ?? (props.adm3 ? rowByCode.get(props.adm3) : undefined)
            ?? (props.adm2 ? rowByCode.get(props.adm2) : undefined)

        if (!row) { setMapPick(null); return }
        setMapPick({ rowId: row.id, lng: lngLat.lng, lat: lngLat.lat, clickedName: area.name })
    }

    // live row for the popup, so edits show at once. A hidden row shows its selected owner's data.
    const pickedRow = useMemo(() => {
        if (!mapPick) return null
        const ownerId = ownerOf.get(mapPick.rowId)
        return rows.find((r) => r.id === (ownerId ?? mapPick.rowId)) ?? null
    }, [mapPick, rows, ownerOf])

    /* ---------- draw a shape -> fill many rows at once ---------- */

    const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])

    // a blank row the bulk popup edits; it is copied onto every covered row on "Apply"
    const newDraft = (): AreaRow => ({
        id: '__bulk__', code: '', parentId: null, depth: 0, name: '', level: '', levelNo: 1,
        previousAlert: 'None', newAlert: 'None',
        rainfall: 0, minWindSpeed: 0, maxWindSpeed: 0, minWaveHeight: 0, maxWaveHeight: 0,
        seaCondition: 'None', lightning: 'None',
        startDate: defaultStart, endDate: defaultEnd, selected: false, continued: false,
    })

    const clearDrawing = () => {
        setDrawing(false)
        drawPtsRef.current = []
        setDrawPts([])
        setBulk(null)
        setBulkDraft(null)
    }

    const startDrawing = () => {
        clearDrawing()
        setMapPick(null)
        setDrawing(true)
    }

    const addDrawPoint = (p: Pt) => {
        drawPtsRef.current = [...drawPtsRef.current, p]
        setDrawPts(drawPtsRef.current)
    }

    // shape closed: find every area at the current zoom level that it covers
    const finishDrawing = (zoom: number) => {
        const pts = cleanPoints(drawPtsRef.current)
        setDrawing(false)
        if (pts.length < 3) { clearDrawing(); return }
        drawPtsRef.current = pts
        setDrawPts(pts)

        const level = levelForZoom(zoom)
        const features: any[] = (data as any)?.[level]?.features ?? []
        const ids: string[] = []
        const seen = new Set<string>()
        let hitCount = 0

        for (const f of features) {
            if (!polygonHitsGeometry(pts, f.geometry)) continue
            hitCount++
            const props = f.properties ?? {}
            // wards are not table rows, so they count for their district / county row
            const row = rowByCode.get(String(props.code))
                ?? (props.adm3 ? rowByCode.get(String(props.adm3)) : undefined)
                ?? (props.adm2 ? rowByCode.get(String(props.adm2)) : undefined)
            if (row && !seen.has(row.id)) {
                seen.add(row.id)
                ids.push(row.id)
            }
        }

        const [lng, lat] = centreOf(pts)
        setBulk({ rowIds: ids, level, hitCount, lng, lat })
        setBulkDraft(newDraft())
    }

    // rows that can't take the values: locked (a child is selected) or hidden (a parent is selected)
    const bulkUsable = bulk ? bulk.rowIds.filter((id) => !isLocked(id) && !hiddenIds.has(id)) : []
    const bulkSkipped = bulk ? bulk.rowIds.length - bulkUsable.length : 0

    const applyBulk = () => {
        if (!bulk || !bulkDraft) return
        const ids = new Set(bulkUsable)
        const d = bulkDraft
        const values: Partial<AreaRow> = {
            newAlert: d.newAlert,
            rainfall: d.rainfall,
            minWindSpeed: d.minWindSpeed,
            maxWindSpeed: d.maxWindSpeed,
            minWaveHeight: d.minWaveHeight,
            maxWaveHeight: d.maxWaveHeight,
            seaCondition: d.seaCondition,
            lightning: d.lightning,
            startDate: d.startDate,
            endDate: d.endDate,
        }
        setRows((prev) =>
            clearSelectedChildren(prev.map((r) => (ids.has(r.id) ? { ...r, ...values, selected: true } : r)))
        )
        clearDrawing()
    }

    // alert and sea condition share the same colour classes
    const alertClass = (v: AlertLevel | SeaConditionLevel) => style[`Alert${v.replace(' ', '')}`]
    const riskClass = (v: LightningRisk) => style[`Risk${v.replace(' ', '')}`]

    const selectable = visible.filter((r) => !isLocked(r.id))
    const allSelected = selectable.length > 0 && selectable.every((r) => r.selected)
    const toggleAll = (checked: boolean) => {
        const ids = new Set(selectable.map((v) => v.id))
        setRows((prev) =>
            clearSelectedChildren(prev.map((r) =>
                !ids.has(r.id) ? r : checked ? { ...r, selected: true } : (r.selected ? resetRow(r) : r)
            ))
        )
    }

    // one cell per column, chosen by the column kind (used by the table AND the map popup)
    const renderCell = (
        row: AreaRow,
        col: ColumnDef,
        disabled = false,
        set: (patch: Partial<AreaRow>) => void = (patch) => updateRow(row.id, patch),
    ) => {
        switch (col.kind) {
            case 'prevAlert':
                return (
                    <div className={style.PrevWrap}>
                        <select className={alertClass(row.previousAlert)} value={row.previousAlert} disabled>
                            {ALERT_LEVELS.map((l) => <option key={l}>{l}</option>)}
                        </select>
                        {row.previousAlert !== 'None' && row.id !== '__bulk__' && (
                            <button
                                type="button"
                                className={`${style.ContinueBtn} ${row.continued ? style.ContinueOn : ''}`}
                                disabled={disabled}
                                title={row.continued ? 'Continuing with the same severity' : 'Continue this active warning with the same severity'}
                                onClick={() => continueOne(row.id)}
                            >
                                {row.continued ? '✓' : '↻'}
                            </button>
                        )}
                    </div>
                )
            case 'newAlert':
                return (
                    <select className={alertClass(row.newAlert)} value={row.newAlert} disabled={disabled} onChange={(e) => set({ newAlert: e.target.value as AlertLevel })}>
                        {ALERT_LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                )
            case 'rainfall':
                return <input type='number' min={0} value={row.rainfall} disabled={disabled} onChange={(e) => set({ rainfall: Number(e.target.value) })} />
            case 'minWindSpeed':
                return <input type='number' min={0} max={row.maxWindSpeed || undefined} value={row.minWindSpeed} disabled={disabled} onChange={(e) => set({ minWindSpeed: Number(e.target.value) })} />
            case 'maxWindSpeed':
                return <input type='number' min={row.minWindSpeed} value={row.maxWindSpeed} disabled={disabled} onChange={(e) => set({ maxWindSpeed: Number(e.target.value) })} />
            case 'minWaveHeight':
                return <input type='number' min={0} step={0.1} max={row.maxWaveHeight || undefined} value={row.minWaveHeight} disabled={disabled} onChange={(e) => set({ minWaveHeight: Number(e.target.value) })} />
            case 'maxWaveHeight':
                return <input type='number' min={row.minWaveHeight} step={0.1} value={row.maxWaveHeight} disabled={disabled} onChange={(e) => set({ maxWaveHeight: Number(e.target.value) })} />
            case 'seaCondition':
                return (
                    <select className={alertClass(row.seaCondition)} value={row.seaCondition} disabled={disabled} onChange={(e) => set({ seaCondition: e.target.value as SeaConditionLevel })}>
                        {SEA_CONDITION_LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                )
            case 'lightning':
                return (
                    <select className={riskClass(row.lightning)} value={row.lightning} disabled={disabled} onChange={(e) => set({ lightning: e.target.value as LightningRisk })}>
                        {LIGHTNING_LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                )
            case 'startDate':
                return <input type='datetime-local' value={row.startDate} disabled={disabled} onChange={(e) => set({ startDate: e.target.value })} />
            case 'endDate':
                return <input type='datetime-local' value={row.endDate} min={row.startDate} disabled={disabled} onChange={(e) => set({ endDate: e.target.value })} />
        }
    }

    return (
        <div className={style.MainWrapper}>
            <div className={style.TabWrapper}>
                <button className={tableMode === 'TABLE' ? style.Select : ''} onClick={() => setTableMode('TABLE')}>Table View Mode</button>
                <button className={tableMode === 'MAP' ? style.Select : ''} onClick={() => setTableMode('MAP')}>Map View Mode</button>
            </div>

            {tableMode === 'TABLE' && (
                <div className={style.TableWrapper}>
                    <div className={style.TopBar}>
                        <div className={style.SearchWrap}>
                            <InputGroup type='text' icon={<SearchIcon width="1rem" color="var(--color-main-white)" />} color={'var(--color-accent-bg)'} placeholder='Search location' componentInputElement={searchRef} onChange={searchData} />
                        </div>

                        <div className={style.FilterSelect}>
                            <label>Level</label>
                            <select value={levelFilter} onChange={(e) => { setLevelFilter(e.target.value); setPage(1) }}>
                                <option value="ALL">All</option>
                                {levelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                        </div>

                        <div className={style.RowCount}>
                            <label>Display Row Count</label>
                            <input type='number' min={5} max={30} ref={rowCountRef} onChange={changeRowCount} />
                        </div>
                    </div>

                    {!loading && !error && activeRows.length > 0 && (
                        <div className={style.ActiveBar}>
                            <span>
                                {activeRows.length} area(s) have an active {warningType} warning
                                {continuedCount > 0 && ` · ${continuedCount} continued`}
                                {changedCount > 0 && ` · ${changedCount} changed`}
                            </span>
                            <button type="button" className={style.ActiveBtn} onClick={clearActive}>Clear</button>
                            <button type="button" className={`${style.ActiveBtn} ${style.ActiveBtnPrimary}`} onClick={continueAll}>
                                Continue all with same severity
                            </button>
                        </div>
                    )}

                    {loading && <p className={style.Status}>Loading locations...</p>}
                    {error && <p className={style.Status}>Failed to load: {error.message}</p>}

                    {!loading && !error && (
                        <>
                            <div className={style.Table}>
                                {/* header row */}
                                <div className={`${style.Row} ${style.Header}`} style={gridStyle}>
                                    <div className={style.Cell}>
                                        <input type='checkbox' checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} />
                                    </div>
                                    <div className={style.Cell}><span>Administration Name</span></div>
                                    <div className={style.Cell}><span>Administration Level</span></div>
                                    {columns.map((c) => (
                                        <div key={c.kind} className={style.Cell}><span>{c.label}</span></div>
                                    ))}
                                </div>

                                {visible.length === 0 && <p className={style.Status}>No locations match your search.</p>}

                                {/* table rows */}
                                {visible.map((row) => (
                                    <div key={row.id} className={`${style.Row} ${style.Body} ${row.selected ? style.RowSelected : ''} ${isLocked(row.id) ? style.RowLocked : ''}`} style={gridStyle}>
                                        <div className={style.Cell}>
                                            <input type='checkbox' checked={row.selected} disabled={isLocked(row.id)} onChange={(e) => toggleRow(row.id, e.target.checked)} />
                                        </div>

                                        {/* Administration Name: guide lines + name */}
                                        <div className={`${style.Cell} ${style.Text}`}>
                                            {row.depth > 0 && (
                                                <span className={style.Tree}>
                                                    {treeGuides.get(row.id)?.ancestors.map((continues, i) => (
                                                        <span key={i} className={`${style.Seg} ${continues ? style.Line : ''}`} />
                                                    ))}
                                                    <span className={`${style.Seg} ${style.Elbow} ${treeGuides.get(row.id)?.hasNext ? style.Through : ''}`} />
                                                </span>
                                            )}
                                            <span className={style.NameBlock}>
                                                <span className={style.NameText} style={isMatch(row) ? { fontWeight: 700 } : undefined}>{row.name}</span>
                                                <span className={style.NameBadges}>
                                                    {row.selected && (hiddenCount.get(row.id) ?? 0) > 0 && (
                                                        <span className={style.Badge}>+{hiddenCount.get(row.id)} included</span>
                                                    )}
                                                    {isLocked(row.id) && (
                                                        <span className={style.LockBadge}>{lockedCount.get(row.id)} selected inside</span>
                                                    )}
                                                    {actionLabel(row) && (
                                                        <span className={`${style.ActionBadge} ${style[`Action${actionLabel(row)!.replace(' ', '')}`] ?? ''}`}>
                                                            {actionLabel(row)}
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </div>

                                        {/* Administration Level: plain text */}
                                        <div className={`${style.Cell} ${style.Text}`}>{row.level}</div>

                                        {columns.map((c) => (
                                            <div key={c.kind} className={style.Cell}>{renderCell(row, c, isLocked(row.id))}</div>
                                        ))}
                                    </div>
                                ))}
                            </div>

                            <div className={style.Pagination}>
                                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>&lt;</button>
                                {getPages(page, totalPages).map((p, i) =>
                                    p === '…'
                                        ? <span key={`gap-${i}`} className={style.Gap}>…</span>
                                        : <button key={p} className={p === page ? style.Select : ''} onClick={() => setPage(p)}>{p}</button>
                                )}
                                <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>&gt;</button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {tableMode === 'MAP' && (
                <div className={style.MapWrapper}>
                    {loading && <p className={style.Status}>Loading locations...</p>}
                    {error && <p className={style.Status}>Failed to load: {error.message}</p>}

                    {!loading && !error && (
                        <LocationMap
                            height="32rem"
                            colors={mapColors}
                            allowIconToggle={false}
                            onAreaClick={onMapAreaClick}
                            cursor={drawing ? 'crosshair' : 'grab'}
                        >
                            <PolygonSelectTool
                                drawing={drawing}
                                points={drawPts}
                                hasShape={!!bulk}
                                onStart={startDrawing}
                                onAddPoint={addDrawPoint}
                                onFinish={finishDrawing}
                                onCancel={clearDrawing}
                            />

                            {/* one popup for every area inside the drawn shape */}
                            {bulk && bulkDraft && (
                                <Popup
                                    className={style.MapPopup}
                                    longitude={bulk.lng}
                                    latitude={bulk.lat}
                                    anchor="left"
                                    offset={12}
                                    closeButton
                                    closeOnClick={false}
                                    onClose={clearDrawing}
                                    maxWidth="440px"
                                >
                                    <div className={style.PopupTitle}>{bulkUsable.length} area(s) in drawn shape</div>
                                    <div className={style.PopupSub}>
                                        {bulk.hitCount} {LEVEL_LABEL[bulk.level]} touched
                                        {bulk.level === 4 && ' (filled on their district rows)'}
                                    </div>
                                    <div className={style.PopupDivider} />

                                    {bulk.rowIds.length === 0 && (
                                        <div className={style.PopupLocked}>No areas found inside the shape. Try zooming out or drawing a bigger shape.</div>
                                    )}

                                    {bulkSkipped > 0 && (
                                        <div className={style.PopupLocked}>
                                            {bulkSkipped} area(s) skipped: a parent or child of them is already selected.
                                        </div>
                                    )}

                                    {bulkUsable.length > 0 && (
                                        <>
                                            <div className={style.PopupNames}>
                                                {bulkUsable.slice(0, 6).map((id) => rowById.get(id)?.name).join(', ')}
                                                {bulkUsable.length > 6 && ` +${bulkUsable.length - 6} more`}
                                            </div>

                                            <div className={style.PopupGrid}>
                                                {columns.filter((c) => c.kind !== 'prevAlert').map((c) => (
                                                    <React.Fragment key={c.kind}>
                                                        <span className={style.PopupLabel}>{c.label}</span>
                                                        <div>
                                                            {renderCell(bulkDraft, c, false, (patch) =>
                                                                setBulkDraft((d) => (d ? { ...d, ...patch } : d))
                                                            )}
                                                        </div>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    <div className={style.PopupButtons}>
                                        <button className={style.DrawBtn} onClick={clearDrawing}>Cancel</button>
                                        <button
                                            className={`${style.DrawBtn} ${style.DrawBtnPrimary}`}
                                            disabled={bulkUsable.length === 0}
                                            onClick={applyBulk}
                                        >
                                            Apply to {bulkUsable.length} area(s)
                                        </button>
                                    </div>
                                </Popup>
                            )}

                            {mapPick && pickedRow && (
                                <Popup
                                    className={style.MapPopup}
                                    longitude={mapPick.lng}
                                    latitude={mapPick.lat}
                                    anchor="left"
                                    offset={12}
                                    closeButton
                                    closeOnClick={false}
                                    onClose={() => setMapPick(null)}
                                    maxWidth="420px"
                                >
                                    <div className={style.PopupTitle}>{pickedRow.name}</div>
                                    <div className={style.PopupSub}>
                                        {pickedRow.level}
                                        {mapPick.clickedName !== pickedRow.name && ` · includes ${mapPick.clickedName}`}
                                    </div>
                                    <div className={style.PopupDivider} />

                                    {isLocked(pickedRow.id) && (
                                        <div className={style.PopupLocked}>
                                            {lockedCount.get(pickedRow.id)} area(s) inside are selected, so this area cannot be changed.
                                        </div>
                                    )}

                                    <label className={`${style.PopupSelect} ${isLocked(pickedRow.id) ? style.PopupDisabled : ''}`}>
                                        <input
                                            type='checkbox'
                                            checked={pickedRow.selected}
                                            disabled={isLocked(pickedRow.id)}
                                            onChange={(e) => toggleRow(pickedRow.id, e.target.checked)}
                                        />
                                        Select this area
                                        {pickedRow.selected && (hiddenCount.get(pickedRow.id) ?? 0) > 0 && (
                                            <span className={style.Badge}>+{hiddenCount.get(pickedRow.id)} included</span>
                                        )}
                                    </label>

                                    <div className={`${style.PopupGrid} ${isLocked(pickedRow.id) ? style.PopupDisabled : ''}`}>
                                        {columns.map((c) => (
                                            <React.Fragment key={c.kind}>
                                                <span className={style.PopupLabel}>{c.label}</span>
                                                <div>{renderCell(pickedRow, c, isLocked(pickedRow.id))}</div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </Popup>
                            )}
                        </LocationMap>
                    )}
                </div>
            )}
        </div>
    )
})

AreaTable.displayName = 'AreaTable'

export default AreaTable